import { Layout, Dropdown, Badge } from 'antd';
import { ShoppingCartOutlined, UserOutlined, LogoutOutlined, MenuOutlined, CloseOutlined, DownOutlined } from '@ant-design/icons';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import { cartService, trainingService, toList, userService } from '../services';
import synteraLogo from '../assets/new-logos/syntera-symbol.png';
import { buildTrainingGroupPath } from '../utils/trainingCatalog';

const CART_CACHE_KEY = 'syntera_cart_cache';
const STAFF_VERIFIED_PATH_PREFIXES = ['/admin-panel', '/yonetim', '/yonetim-paneli', '/studio', '/egitmen-studyosu'];

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [cartCount, setCartCount] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [trainingItems, setTrainingItems] = useState([]);
  const [trainingItemsLoading, setTrainingItemsLoading] = useState(false);
  const [trainingItemsLoaded, setTrainingItemsLoaded] = useState(false);
  const isAuthenticated = !!localStorage.getItem('access_token');
  const isStaff = localStorage.getItem('is_staff') === 'true';
  const isStaffVerifiedRoute = STAFF_VERIFIED_PATH_PREFIXES.some((prefix) => location.pathname.startsWith(prefix));
  const shouldSkipHeaderCartFetch = location.pathname === '/cart';

  useEffect(() => {
    if (isAuthenticated) {
      if (!isStaffVerifiedRoute) {
        syncRoleState();
      }
    }
  }, [isAuthenticated, isStaffVerifiedRoute]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const handleCartUpdated = () => {
      fetchCartCount();
    };

    window.addEventListener('syntera-cart-updated', handleCartUpdated);
    return () => {
      window.removeEventListener('syntera-cart-updated', handleCartUpdated);
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && !shouldSkipHeaderCartFetch) {
      fetchCartCount();
    }
  }, [isAuthenticated, shouldSkipHeaderCartFetch, location.pathname]);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const loadTrainingItems = async () => {
    if (trainingItemsLoaded || trainingItemsLoading) {
      return;
    }

    setTrainingItemsLoading(true);
    try {
      const response = await trainingService.getItems();
      setTrainingItems(toList(response.data));
      setTrainingItemsLoaded(true);
    } catch {
      setTrainingItems([]);
    } finally {
      setTrainingItemsLoading(false);
    }
  };

  const fetchCartCount = async () => {
    try {
      const response = await cartService.getCart();
      setCartCount(response.data.items?.length || 0);
    } catch {
      try {
        const cached = JSON.parse(localStorage.getItem(CART_CACHE_KEY) || '{}');
        setCartCount(Array.isArray(cached.items) ? cached.items.length : 0);
      } catch {
        setCartCount(0);
      }
    }
  };

  const syncRoleState = async () => {
    if (localStorage.getItem('is_staff')) return;
    try {
      const response = await userService.getProfile();
      localStorage.setItem('is_staff', String(!!response.data.is_staff));
    } catch { /* silent */ }
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('is_staff');
    navigate('/login');
  };

  const userMenu = [
    { key: 'profile', label: <Link to="/profil">Profilim</Link> },
    { key: 'purchases', label: <Link to="/egitimlerim">Satın Aldıklarım</Link> },
    { key: 'certificates', label: <Link to="/sertifikalarim">Sertifikalarım</Link> },
    ...(isStaff ? [
      { key: 'admin-panel', label: <Link to="/yonetim">Yönetim Merkezi</Link> },
      { key: 'studio', label: <Link to="/egitmen-studyosu">İçerik Stüdyosu</Link> },
    ] : []),
    { type: 'divider' },
    { key: 'logout', label: 'Çıkış Yap', icon: <LogoutOutlined />, onClick: handleLogout },
  ];

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const trainingGroups = useMemo(() => trainingItems.reduce((accumulator, item) => {
    const groupTitle = item.group_title || 'Diğer Eğitimler';
    if (!accumulator[groupTitle]) {
      accumulator[groupTitle] = [];
    }
    accumulator[groupTitle].push(item);
    return accumulator;
  }, {}), [trainingItems]);

  const trainingGroupEntries = useMemo(() => Object.entries(trainingGroups), [trainingGroups]);

  const navLinks = [
    { to: '/', label: 'Anasayfa', exact: true },
    { to: '/egitimlerimiz', label: 'Eğitimlerimiz' },
    { to: '/akademilerimiz', label: 'Akademilerimiz' },
    { to: '/danismanlik-konularimiz', label: 'Danışmanlık Konularımız' },
    { to: '/hakkimizda', label: 'Hakkımızda' },
    { to: '/blog', label: 'Blog' },
    { to: '/iletisim', label: 'İletişim' },
  ];

  return (
    <Layout.Header className="syntera-nav-header">
      <div className="syntera-nav-inner">
        {/* Logo */}
        <Link to="/" className="syntera-nav-logo syntera-nav-logo--symbol">
          <img src={synteraLogo} alt="Syntera" className="syntera-nav-logo-symbol" />
        </Link>

        {/* Desktop Nav */}
        <nav className="syntera-nav-links">
          {navLinks.map((link) => (
            link.to === '/egitimlerimiz' ? (
              <div
                key={link.to}
                className="syntera-nav-training-dropdown"
                onMouseEnter={loadTrainingItems}
                onFocus={loadTrainingItems}
              >
                <Link
                  to={link.to}
                  className={`syntera-nav-link syntera-nav-training-trigger ${isActive(link.to) ? 'active' : ''}`}
                >
                  <span>{link.label}</span>
                  <DownOutlined className="syntera-nav-training-caret" />
                </Link>
                {trainingGroupEntries.length ? (
                  <div className="syntera-nav-training-panel">
                    {trainingGroupEntries.map(([groupTitle, items]) => (
                      <div key={groupTitle} className="syntera-nav-training-column">
                        <Link
                          to={buildTrainingGroupPath(groupTitle)}
                          className="syntera-nav-training-group-link"
                        >
                          {groupTitle}
                        </Link>
                        <div className="syntera-nav-training-meta">{items.length} eğitim</div>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : (
              <Link
                key={link.to}
                to={link.to}
                className={`syntera-nav-link ${
                  link.exact ? location.pathname === '/' && link.to === '/' ? 'active' : ''
                  : isActive(link.to) ? 'active' : ''
                }`}
              >
                {link.label}
              </Link>
            )
          ))}
          {isAuthenticated && (
            <Link to="/egitimlerim" className={`syntera-nav-link ${(isActive('/purchases') || isActive('/egitimlerim')) ? 'active' : ''}`}>
              Eğitimlerim
            </Link>
          )}
          {isStaff && (
            <Link to="/yonetim" className={`syntera-nav-link syntera-nav-link--staff ${(isActive('/admin-panel') || isActive('/yonetim') || isActive('/yonetim-paneli')) ? 'active' : ''}`}>
              Yönetim
            </Link>
          )}
        </nav>

        {/* Desktop Actions */}
        <div className="syntera-nav-actions">
          {isAuthenticated && (
            <>
              <Link to="/cart" className="syntera-nav-icon-btn" title="Sepet">
                <Badge count={cartCount} size="small">
                  <ShoppingCartOutlined />
                </Badge>
              </Link>
            </>
          )}

          {isAuthenticated ? (
            <Dropdown menu={{ items: userMenu }} trigger={['click']} placement="bottomRight">
              <button className="syntera-nav-user-btn">
                <UserOutlined />
                <span>Hesabım</span>
              </button>
            </Dropdown>
          ) : (
            <>
              <Link to="/login" className="syntera-nav-btn-ghost">Giriş Yap</Link>
              <Link to="/register" className="syntera-nav-btn-primary">Kayıt Ol</Link>
            </>
          )}
        </div>

        {/* Mobile Toggle */}
        {!isAuthenticated ? (
          <div className="syntera-nav-mobile-auth-inline">
            <Link to="/login" className="syntera-nav-btn-ghost syntera-nav-btn-ghost--mobile">Giriş Yap</Link>
            <Link to="/register" className="syntera-nav-btn-primary syntera-nav-btn-primary--mobile">Kayıt Ol</Link>
          </div>
        ) : null}

        <button className="syntera-nav-mobile-toggle" onClick={() => setMobileOpen(!mobileOpen)}>
          {mobileOpen ? <CloseOutlined /> : <MenuOutlined />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="syntera-nav-mobile">
          {isAuthenticated && (
            <div className="syntera-nav-mobile-shortcuts">
              <Link to="/egitimlerim" className="syntera-nav-mobile-shortcut">Eğitimlerim</Link>
              <Link to="/iletisim" className="syntera-nav-mobile-shortcut">İletişim</Link>
            </div>
          )}
          {navLinks.map((link) => (
            <Link key={link.to} to={link.to} className="syntera-nav-mobile-link">{link.label}</Link>
          ))}
          {isAuthenticated && <Link to="/egitimlerim" className="syntera-nav-mobile-link">Eğitimlerim</Link>}
          {isStaff && <Link to="/yonetim" className="syntera-nav-mobile-link">Yönetim Merkezi</Link>}
          <div className="syntera-nav-mobile-divider" />
          {isAuthenticated ? (
            <button className="syntera-nav-mobile-logout" onClick={handleLogout}>Çıkış Yap</button>
          ) : (
            <>
              <Link to="/login" className="syntera-nav-mobile-link">Giriş Yap</Link>
              <Link to="/register" className="syntera-nav-btn-primary" style={{ textAlign: 'center', display: 'block', margin: '8px 16px' }}>Kayıt Ol</Link>
            </>
          )}
        </div>
      )}
    </Layout.Header>
  );
};

export default Header;

